<template>
  <div class="flex flex-col items-center py-8 px-4">
    <div class="w-full max-w-lg">
      <div class="mb-6">
        <h2 class="text-base font-semibold text-gray-800">Reservation Expiry Settings</h2>
        <p class="text-sm text-gray-500 mt-1">Configure how long name reservations remain active before they expire. Set
          expiry periods for reservation types and define notification windows.</p>
      </div>
      <div class="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <form class="space-y-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Period</label>
            <input
              type="number"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
              placeholder="Enter number"
              v-model="reservation.value.period"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</label>
            <select v-model="reservation.key"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="option in types" :key="option.value ?? ''" :value="option.value">{{ option.text }}</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Unit</label>
            <select v-model="reservation.value.type"
                    class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary">
              <option v-for="option in options" :key="option.value ?? ''" :value="option.value">{{
                  option.text
                }}
              </option>
            </select>
          </div>
          <div class="border-t border-gray-100 pt-2 flex justify-end">
            <button type="button" class="btn-primary" @click="addExpiryTime">
              <i class="fa fa-plus"></i>
              Add Expiry Time
            </button>
          </div>
        </form>
      </div>

      <div class="mt-6">
        <table class="min-w-full leading-normal">
          <thead>
          <tr class="border-b border-t border-gray-200">
            <th class="t-header">Name</th>
            <th class="t-header">Quantity</th>
            <th class="t-header">Unit</th>
            <th class="t-header">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(item, idx) in expiryTimes" :key="idx">
            <td class="t-body">{{ item.key.replaceAll("_", " ") }}</td>
            <td class="t-body">{{ item.value.period }}</td>
            <td class="t-body text-capitalize">{{ item.value.type.toLowerCase() }}</td>
            <td class="t-body">
              <button @click="removeExpiryTime(item)" class="btn-sm-danger">
                <i class="fa fa-trash mr-1"></i> Remove
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../stores";

interface ReservationForm {
  key: string | null;
  value: {
    period: number
    type: string | null
  };
}

interface ExpiryTime {
  key: string;
  value: {
    period: number
    type: string
  };
}

const store = useConfigurationsStore();

const loading = ref(false);
const reservation = ref<ReservationForm>({
  key: null,
  value: {
    period: 0,
    type: null
  }
});
const types = ref([
  { text: "- Select Type -", value: null },
  { text: "ASSIGNED EXPIRY DATE", value: "ASSIGNED_EXPIRY_DATE" },
  { text: "DRAFT EXPIRY DATE", value: "DRAFT_EXPIRY_DATE" },
  { text: "EXPIRY NOTIFICATION", value: "EXPIRY_NOTIFICATION" },
  { text: "PENDING APPLICATION EXPIRY DATE", value: "PENDING_APPLICATION_EXPIRY_DATE" }
]);
const options = ref([
  { text: "- Select Unit -", value: null },
  { text: "DAYS", value: "DAYS" },
  { text: "HOURS", value: "HOURS" },
  { text: "MINUTES", value: "MINUTES" },
  { text: "MONTHS", value: "MONTHS" },
  { text: "YEARS", value: "YEARS" }
]);
const expiryTimes = ref<ExpiryTime[]>([]);
const rows = ref({
  odd: "border-t border-gray-200 hover:bg-gray-10",
  even: "border-t border-gray-200 bg-gray-10"
});

onMounted(() => {
  store.fetchReservationExpiryTimes();
});

const addExpiryTime = () => {
  loading.value = true;
  if (reservation.value.key === "EXPIRY_NOTIFICATION") {
    store.addReservationNotificationTime(reservation.value);
  } else {
    store.addReservationExpiryTime(reservation.value);
  }
};

const removeExpiryTime = (item: ExpiryTime) => {
  expiryTimes.value = expiryTimes.value.filter(i => i !== item);
};

watch(
  () => store.reservationAddStatus,
  () => {
    // @ts-ignore - successToast is injected globally
    window.successToast();
    loading.value = false;
    store.fetchReservationExpiryTimes();
  }
);

watch(
  () => store.reservationExpiryTimes,
  (data) => {
    expiryTimes.value = data;
  }
);

watch(
  () => store.reservationAddError,
  (error) => {
    loading.value = false;
    // @ts-ignore - errorToast is injected globally
    window.errorToast("Error", error.message || "something went wrong");
  }
);
</script>

